import { Routes as Router, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import MeetRedirect from '@pages/CommonPages/MeetRedirect';
import StartLinkRedirect from '@pages/CommonPages/StartLinkRedirect';
import type { Location } from 'react-router-dom';
import { lazy } from 'react';

import useDocumentTitle from 'lib.hooks/src/useDocumentTitle';
import { useAuth } from 'lib.context/src/AuthContext';
// import { DrawerProvider } from 'lib.context/src/DrawerContext';
import LoadingView from 'lib.components/src/components/common/LoadingView';
import { UserType } from 'lib.store/src/models-to-replace/auth';
import { home, homeRouteFor, login } from 'lib.shared/src/routes';
import { CampaignEditorProvider } from 'lib.context/src/CampaignEditorContext';
import { EventEditorContextProvider } from 'lib.context/src/forms/event/EventEditorContext';
import { LandingEditorContextProvider } from 'lib.context/src/LandingEditorContext';
import { InfiniteScrollProvider } from 'lib.context/src/InfiniteScrollContext';
import { CreativeTemplatesProvider } from '@pages/OrganizerRolePages/EventPage/setup/creative-templates/context/CreativeTemplatesCtx';
import {
  ORGANIZATION_TYPE,
  type OrganizationType,
} from 'lib.store/src/main/organization/organizationType';

// Lazy-loaded pages
const LoginPage = lazy(() => import('lib.pages/src/Authentification/Login'));
const MyEventEditPage = lazy(
  () => import('@pages/PartnerRolePages/MyEvents/pages/MyEventEditPage'),
);
const EventLeadsPage = lazy(() => import('@pages/PartnerRolePages/Leads'));
const LeadQualificationsPage = lazy(() => import('@pages/PartnerRolePages/LeadQualifications'));
const MyEventsCatalogPage = lazy(
  () => import('@pages/PartnerRolePages/MyEvents/pages/MyEventsCatalog'),
);
const MyEventsCatalogEventItemPage = lazy(
  () => import('@pages/PartnerRolePages/MyEvents/pages/MyEventsCatalogEventItem'),
);
const MyEventsCardsPage = lazy(
  () => import('@pages/PartnerRolePages/MyEvents/pages/MyEventsCardsPage'),
);
const EventCampaignsPage = lazy(() => import('@pages/PartnerRolePages/Campaigns'));
const LandingPagesListPage = lazy(() => import('@pages/PartnerRolePages/LandingPages'));
// import LandingPageConstructor from '@pages/PartnerRolePages/LandingPageConstructor';
const AnalyticsPage = lazy(() => import('lib.pages/src/Analytics'));

// import AnalyticsEventsPage from '@pages/OrganizerRolePages/AnalyticsEventsPage';

const ModerationPage = lazy(() => import('@pages/OrganizerRolePages/ModerationPage'));
const PartnersPage = lazy(() => import('@pages/OrganizerRolePages/PartnersPage'));
const AudiencesEventPage = lazy(() => import('@pages/CommonPages/AudiencesPage'));
const CampaignEditorPage = lazy(
  () => import('@pages/PartnerRolePages/Campaigns/CampaignEditorPage'),
);
const NotFoundPage = lazy(() => import('@pages/CommonPages/NotFoundPage'));
const EventOnboarding = lazy(() => import('lib.pages/src/Onboarding/EventOnboarding'));
const EventsPage = lazy(() => import('@pages/OrganizerRolePages/EventPage'));
// import SolisticaPreview from '@styled-preview/Solistica';
const PartnerOnboarding = lazy(() => import('lib.pages/src/Onboarding/PartnerOnboarding'));
const PresentationStartPage = lazy(() => import('lib.pages/src/PresentationStart'));
const MailingUnsubscribePage = lazy(() => import('lib.pages/src/MailingUnsubscribe'));
const BoostProPage = lazy(() => import('@pages/PartnerRolePages/BoostPro'));
const JoinRequestPage = lazy(() => import('@pages/PartnerRolePages/JoinRequest'));
const MediaPlanPage = lazy(() => import('@pages/PartnerRolePages/MediaPlan'));
const CreativesDeskPage = lazy(() => import('@pages/PartnerRolePages/CreativesDesk'));
const CreativeIdeasPage = lazy(() => import('@pages/PartnerRolePages/CreativeIdeas'));
const MediaPlanCreativesPage = lazy(() => import('@pages/PartnerRolePages/MediaPlanCreatives'));
const BriefPage = lazy(() => import('@pages/PartnerRolePages/Brief'));
const RegistrationPage = lazy(() => import('lib.pages/src/Authentification/Register'));
// Stripe/UserBilling providers are supplied globally by BillingProvider in App
const EmailConfirmationSuccessPage = lazy(
  () => import('lib.components/src/components/EmailConfirmation/EmailConfirmationSuccess'),
);
const LandingCreationMethod = lazy(
  () => import('@pages/PartnerRolePages/LandingCreationMethod/LandingCreationMethod'),
);
const LandingPageConstructor = lazy(() => import('@pages/PartnerRolePages/LandingPageConstructor'));
const NotificationsPage = lazy(() => import('@pages/PartnerRolePages/Notifications'));
const SettingsPage = lazy(() => import('@pages/PartnerRolePages/SettingsPage'));
const PasswordRecoverPage = lazy(() => import('lib.pages/src/Authentification/PasswordRecover'));
const PasswordChangePage = lazy(
  () => import('lib.pages/src/Authentification/PasswordUpdateConfirm'),
);
const LoginLinkPage = lazy(() => import('lib.pages/src/Authentification/LoginLink'));
const LoginCheckPage = lazy(() => import('lib.pages/src/Authentification/LoginCheck'));
const PasswordChangeFromRedirectPage = lazy(
  () =>
    import('lib.pages/src/Authentification/PasswordUpdateConfirm/PasswordChangeFromRedirectPage'),
);

const PrivacyPolicy = lazy(() => import('lib.pages/src/ToSPages/PrivacyPolicy'));
const TermsAndConditions = lazy(() => import('lib.pages/src/ToSPages/TermsOfService'));
const CookiePolicy = lazy(() => import('lib.pages/src/ToSPages/CookiePolicy'));
const CommonTermsOfServicePage = lazy(
  () => import('lib.pages/src/ToSPages/CommonTermsOfServicePage'),
);

const DEFAULT_TITLE = 'Boost Express';
const ROUTE_TITLES: Record<string, string> = {
  '/login': 'Login',
  '/registration': 'Registration',
  '/settings': 'Settings',
  '/billing': 'Billing',
  '/notifications': 'Notifications',
  '/events': 'My Events',
  '/campaigns': 'Campaigns',
  '/leads': 'Leads',
  '/lead-qualifications': 'Lead Qualifications',
  '/audiences': 'Audiences',
  '/analytics': 'Analytics',
  '/pro': 'Boost PRO',
  '/landing-pages': 'Landing Pages',
  '/privacy-policy': 'Privacy Policy',
  '/cookie-policy': 'Cookie Policy',
  '/terms-and-conditions': 'Terms and Conditions',
  '/shortlist': 'Shortlist',
  '/chats': 'Chats',
  '/meetings': 'Meetings',
  '/memory': 'Memory',
  '/matching': 'Matching',
  '/admin': 'Admin',
  '/start/partner': 'Your campaign desk',
};

const InitPage = lazy(() => import('lib.pages/src/Authentification/InitPage'));
const BillingPartner = lazy(() => import('@pages/PartnerRolePages/Billing'));
const EventPage = lazy(() => import('@pages/OrganizerRolePages/EventPage/setup'));
const EventsSetupPage = lazy(() => import('@pages/OrganizerRolePages/EventPage/create'));
const EventPreview = lazy(() => import('@pages/OrganizerRolePages/EventPage/setup/preview'));
const VideoTourPage = lazy(() => import('@pages/PartnerRolePages/VideoTour'));
const SponsorServiceAgreementPage = lazy(
  () => import('@pages/CommonPages/SponsorServiceAgreementPage/SponsorServiceAgreementPage.tsx'),
);
const EventServiceAgreementPage = lazy(
  () => import('@pages/CommonPages/EventServiceAgreementPage/EventServiceAgreementPage.tsx'),
);
const AudiencesEditPage = lazy(() => import('@pages/OrganizerRolePages/AudiencesEditPage'));
const EventSubdomainMobile = lazy(
  () => import('@pages/OrganizerRolePages/EventPage/mobile/setup/subdomain'),
);
const EventPageSetupMobile = lazy(() => import('@pages/OrganizerRolePages/EventPage/mobile/setup'));
const EventSetupAccountsMobile = lazy(
  () => import('@pages/OrganizerRolePages/EventPage/mobile/setup/ad-accounts'),
);
const EventSetupSponsorsMobile = lazy(
  () => import('@pages/OrganizerRolePages/EventPage/mobile/setup/sponsors'),
);
const EventSetupCampaignsMobile = lazy(
  () => import('@pages/OrganizerRolePages/EventPage/mobile/setup/campaigns'),
);
const EventSetupProductsMobile = lazy(
  () => import('@pages/OrganizerRolePages/EventPage/mobile/setup/products'),
);
const EventSetupGeneralMobile = lazy(
  () => import('@pages/OrganizerRolePages/EventPage/mobile/setup/general'),
);
const EventSetupTrackMobile = lazy(
  () => import('@pages/OrganizerRolePages/EventPage/mobile/setup/track'),
);
const EventSetupBillingMobile = lazy(
  () => import('@pages/OrganizerRolePages/EventPage/mobile/setup/billing'),
);
const EventSetupAudienceCatalogMobile = lazy(
  () => import('@pages/OrganizerRolePages/EventPage/mobile/setup/audience-catalog'),
);
const EventLandingPageTemplatesMobile = lazy(
  () => import('@pages/OrganizerRolePages/EventPage/mobile/setup/landing-page-templates'),
);
const SegmentEditPage = lazy(() => import('@pages/CommonPages/SegmentEditPage'));
const EventSetupModerationMobile = lazy(
  () => import('@pages/OrganizerRolePages/EventPage/mobile/setup/moderation'),
);
const CreativesFullscreenPage = lazy(
  () =>
    import('@pages/OrganizerRolePages/EventPage/setup/creative-templates/CreativesFullscreenPage'),
);
const EventSetupCreativeTemplatesMobile = lazy(
  () => import('@pages/OrganizerRolePages/EventPage/mobile/setup/creative-templates'),
);

// Attendee Pages
const AttendeeEventsPage = lazy(() => import('@pages/AttendeeRolePages/Events'));
const AttendeeShortlistPage = lazy(() => import('@pages/AttendeeRolePages/Shortlist'));
const AttendeeMatchesPage = lazy(() => import('@pages/AttendeeRolePages/Matches'));
const AttendeeChatsPage = lazy(() => import('@pages/AttendeeRolePages/Chats'));
const AttendeeChatPage = lazy(() => import('@pages/AttendeeRolePages/Chats/ChatPage'));
const AttendeeCompanyPage = lazy(() => import('@pages/AttendeeRolePages/Company'));
const AttendeeMeetingsPage = lazy(() => import('@pages/AttendeeRolePages/Meetings'));
const AttendeeMemoryPage = lazy(() => import('@pages/AttendeeRolePages/Memory'));
const AttendeeIntentGate = lazy(() => import('./components/AttendeeIntentGate'));

// Admin Pages
const AdminPage = lazy(() => import('@pages/AdminPages'));

const PrivateRoutes = () => {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <Outlet />;
};

/**
 * The redirects every role table ends with: a bare "/" and anything unmatched
 * both land on that role's home page, taken from HOME_ROUTE_BY_ORGANIZATION_TYPE
 * (lib.shared/src/routes) — Match.Express opens on matching, Boost.Express on
 * My events. Moving a landing page is one edit there, not one per table.
 *
 * They stay INSIDE each table (rather than wrapping all of them once) because
 * the attendee table nests its pages in AttendeeIntentGate: a redirect fired
 * from outside that gate would drop an outreach link's query string before the
 * gate could keep the token.
 *
 * @param type organization type whose home page these redirects point at
 */
const homeRedirects = (type: OrganizationType) => [
  <Route key="home" path={home} element={<Navigate to={homeRouteFor(type)} replace />} />,
  <Route key="rest" path="/*" element={<Navigate to={homeRouteFor(type)} replace />} />,
];

/**
 * The start page — the personal link's and the event's shared one — public and shareable, registered in every
 * table, mid-onboarding included. The two retired surfaces of the same link
 * (the deck and the pitch page) forward to it, so links already sent keep
 * working. The same mail's unsubscribe link rides along: whoever opens it,
 * signed in or not, must land on it.
 */
const startRoutes = () => [
  <Route key="start" path="/start/partner/:token" element={<PresentationStartPage />} />,
  // the event's shared link — the default start page, the visitor picks their company first
  <Route key="start-event" path="/start/partner/ads/:token" element={<PresentationStartPage />} />,
  <Route key="presentation" path="/presentation/partner/:token" element={<StartLinkRedirect />} />,
  <Route key="pitch" path="/pitch/partner/:token" element={<StartLinkRedirect />} />,
  <Route key="unsubscribe" path="/unsubscribe/:token" element={<MailingUnsubscribePage />} />,
];

//handle routes by organisation type: "admin" | "none" | "partner" | "event"
const OrganizationRoutesByType = {
  admin: (
    <Route element={<PrivateRoutes />}>
      {/* Admin Panel */}
      <Route path="/admin" element={<AdminPage />} />
      <Route path="/admin/:tabId" element={<AdminPage />} />
      <Route path="/admin/:tabId/:id" element={<AdminPage />} />
      {/* detail pages whose edit mode lives in the URL (e.g. artifacts/:id/edit) */}
      <Route path="/admin/:tabId/:id/:action" element={<AdminPage />} />
      {/* Settings (preserved) */}
      <Route path="/settings" element={<SettingsPage />} />
      <Route path="/settings/:tabId" element={<SettingsPage />} />
      <Route path="/settings/:tabId/:billingId" element={<SettingsPage />} />
      <Route path="/login/link/:token" element={<LoginLinkPage />} />
      {startRoutes()}
      {homeRedirects(ORGANIZATION_TYPE.admin)}
    </Route>
  ),
  none: (
    <Route element={<PrivateRoutes />}>
      <Route path="/registration" element={<RegistrationPage />} />
      <Route path="/registration/event" element={<RegistrationPage />} />
      <Route path="/registration/attendee" element={<RegistrationPage />} />
      <Route path="/m/:eventId/:partnerId" element={<MeetRedirect />} />
      <Route path="/pass_recover" element={<PasswordRecoverPage />} />
      <Route path="/pass_recover/event" element={<PasswordRecoverPage />} />
      <Route path="/pass_recover/attendee" element={<PasswordRecoverPage />} />
      <Route path="/pass_change" element={<PasswordChangePage />} />
      <Route path="/pass_recover_confirm" element={<PasswordChangeFromRedirectPage />} />
      <Route path="/login/link/:token" element={<LoginLinkPage />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/cookie-policy" element={<CookiePolicy />} />
      <Route path="/email-confirmed" element={<EmailConfirmationSuccessPage />} />
      <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
      <Route path="/common-terms-and-conditions" element={<CommonTermsOfServicePage />} />
      <Route path="/sponsor-service-agreement" element={<SponsorServiceAgreementPage />} />
      <Route path="/event-service-agreement" element={<EventServiceAgreementPage />} />
      <Route path="/onboarding/partner/:hash" element={<PartnerOnboarding />} />
      {startRoutes()}
      {homeRedirects(ORGANIZATION_TYPE.none)}
    </Route>
  ),
  partner: (
    <Route element={<PrivateRoutes />}>
      <Route path="/events" element={<MyEventsCardsPage />} />
      <Route path="/events/catalog/:id" element={<MyEventsCatalogEventItemPage />} />
      <Route
        path="/events/catalog"
        element={
          <InfiniteScrollProvider>
            <MyEventsCatalogPage />
          </InfiniteScrollProvider>
        }
      />
      <Route path="/events/edit/:id" element={<MyEventEditPage />} />
      <Route path="/events/edit/:id/:tabId" element={<MyEventEditPage />} />
      {/* Campaigns */}
      <Route
        path="/campaigns"
        element={
          <InfiniteScrollProvider>
            <EventCampaignsPage />
          </InfiniteScrollProvider>
        }
      />
      <Route
        path="/campaigns/create"
        element={
          <CampaignEditorProvider>
            <CampaignEditorPage />
          </CampaignEditorProvider>
        }
      />
      <Route
        path="/campaigns/create/:tabId"
        element={
          <CampaignEditorProvider>
            <CampaignEditorPage />
          </CampaignEditorProvider>
        }
      />
      <Route
        path="/campaigns/create/:tabId/:creativeId"
        element={
          <CampaignEditorProvider>
            <CampaignEditorPage />
          </CampaignEditorProvider>
        }
      />
      <Route
        path="/campaigns/:campaignId"
        element={
          <CampaignEditorProvider>
            <CampaignEditorPage />
          </CampaignEditorProvider>
        }
      />
      <Route
        path="/campaigns/:campaignId/groups/:groupId/creatives"
        element={
          <CampaignEditorProvider>
            <CampaignEditorPage />
          </CampaignEditorProvider>
        }
      />
      <Route
        path="/campaigns/:campaignId/groups/:groupId/creatives/:creativeId"
        element={
          <CampaignEditorProvider>
            <CampaignEditorPage />
          </CampaignEditorProvider>
        }
      />
      <Route
        path="/campaigns/:campaignId/:tabId"
        element={
          <CampaignEditorProvider>
            <CampaignEditorPage />
          </CampaignEditorProvider>
        }
      />
      <Route
        path="/campaigns/:campaignId/:tabId/:creativeId"
        element={
          <CampaignEditorProvider>
            <CampaignEditorPage />
          </CampaignEditorProvider>
        }
      />

      <Route
        path="/leads"
        element={
          <InfiniteScrollProvider>
            <EventLeadsPage />
          </InfiniteScrollProvider>
        }
      />
      <Route path="/lead-qualifications" element={<LeadQualificationsPage />} />
      <Route
        path="/landing-pages"
        element={
          <InfiniteScrollProvider>
            <LandingPagesListPage />
          </InfiniteScrollProvider>
        }
      />
      <Route path="/pro" element={<BoostProPage />} />
      {/* a request to join the account from the start page — where its email takes the admin */}
      <Route path="/join-requests/:id" element={<JoinRequestPage />} />
      <Route path="/ideas" element={<CreativeIdeasPage />} />
      <Route path="/creatives" element={<CreativesDeskPage />} />
      <Route path="/media-plan-creatives" element={<MediaPlanCreativesPage />} />
      <Route path="/brief" element={<BriefPage />} />
      <Route
        path="/media-plan"
        element={
          <InfiniteScrollProvider>
            <MediaPlanPage />
          </InfiniteScrollProvider>
        }
      />
      <Route
        path="/media-plan/:campaignId/groups/:groupId/creatives/:creativeId"
        element={
          <InfiniteScrollProvider>
            <MediaPlanPage />
          </InfiniteScrollProvider>
        }
      />
      <Route
        path="/media-plan/:campaignId/groups/:groupId/creatives"
        element={
          <InfiniteScrollProvider>
            <MediaPlanPage />
          </InfiniteScrollProvider>
        }
      />
      <Route
        path="/media-plan/:campaignId/:tabId?/:creativeId?"
        element={
          <InfiniteScrollProvider>
            <MediaPlanPage />
          </InfiniteScrollProvider>
        }
      />
      <Route path="/video-tour" element={<VideoTourPage />} />

      <Route path="/analytics" element={<AnalyticsPage />} />

      {/* Landing Pages */}
      <Route
        path="/landing-pages/create"
        element={
          <LandingEditorContextProvider>
            <LandingCreationMethod />
          </LandingEditorContextProvider>
        }
      />
      <Route
        path="/landing-pages/create/:type"
        element={
          <LandingEditorContextProvider>
            <LandingPageConstructor />
          </LandingEditorContextProvider>
        }
      />
      <Route
        path="/landing-pages/:landingId/edit"
        element={
          <LandingEditorContextProvider>
            <LandingPageConstructor />
          </LandingEditorContextProvider>
        }
      />

      <Route path="/billing" element={<BillingPartner />} />
      <Route path="/billing/:tabId" element={<BillingPartner />} />

      <Route path="/audiences" element={<AudiencesEventPage />} />
      <Route path="/audiences/:audienceId" element={<AudiencesEventPage />} />
      <Route path="/audiences/segment/create" element={<SegmentEditPage />} />
      <Route path="/audiences/segment/edit/:segmentId" element={<SegmentEditPage />} />

      <Route path="/settings" element={<SettingsPage />} />
      <Route path="/settings/:tabId" element={<SettingsPage />} />
      <Route path="/settings/:tabId/:billingId" element={<SettingsPage />} />

      <Route path="/registration" element={<RegistrationPage />} />

      <Route path="/email-confirmed" element={<EmailConfirmationSuccessPage />} />
      <Route path="/pass_recover" element={<PasswordRecoverPage />} />
      <Route path="/pass_recover/event" element={<PasswordRecoverPage />} />
      <Route path="/pass_recover/attendee" element={<PasswordRecoverPage />} />
      <Route path="/pass_recover_confirm" element={<PasswordChangeFromRedirectPage />} />
      <Route path="/login/link/:token" element={<LoginLinkPage />} />

      <Route path="/notifications" element={<NotificationsPage />} />

      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/cookie-policy" element={<CookiePolicy />} />
      <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
      <Route path="/common-terms-and-conditions" element={<CommonTermsOfServicePage />} />
      <Route path="/sponsor-service-agreement" element={<SponsorServiceAgreementPage />} />
      <Route path="/event-service-agreement" element={<EventServiceAgreementPage />} />
      <Route path="/onboarding/partner/:hash" element={<PartnerOnboarding />} />
      {startRoutes()}
      {homeRedirects(ORGANIZATION_TYPE.partner)}
    </Route>
  ),

  organizer: (
    <Route element={<PrivateRoutes />}>
      <Route
        path="/events"
        element={
          <InfiniteScrollProvider>
            <EventsPage />
          </InfiniteScrollProvider>
        }
      />
      <Route
        path="/events/info/:eventId/:tabId"
        element={
          <EventEditorContextProvider>
            <EventPage />
          </EventEditorContextProvider>
        }
      />
      <Route
        path="/events/info/:eventId/creative_templates/:creativeId/fullscreen"
        element={
          <CreativeTemplatesProvider>
            <CreativesFullscreenPage />
          </CreativeTemplatesProvider>
        }
      />

      <Route
        path="/events/setup"
        element={
          <EventEditorContextProvider>
            <EventsSetupPage />
          </EventEditorContextProvider>
        }
      />
      <Route
        path="/events/edit/:eventId"
        element={
          <EventEditorContextProvider>
            <EventsSetupPage />
          </EventEditorContextProvider>
        }
      />

      {/** mobile setup **/}
      <Route
        path="/events/info/:eventId/setup"
        element={
          <EventEditorContextProvider>
            <EventPageSetupMobile />
          </EventEditorContextProvider>
        }
      />
      <Route
        path="/events/info/:eventId/setup/general"
        element={
          <EventEditorContextProvider>
            <EventSetupGeneralMobile />
          </EventEditorContextProvider>
        }
      />
      <Route
        path="/events/info/:eventId/setup/billing_defined"
        element={<EventSetupBillingMobile />}
      />
      <Route
        path="/events/info/:eventId/setup/landing_template_configured"
        element={<EventLandingPageTemplatesMobile />}
      />
      <Route
        path="/events/info/:eventId/setup/track_installed"
        element={<EventSetupTrackMobile />}
      />
      <Route
        path="/events/info/:eventId/setup/partners"
        element={
          <EventEditorContextProvider>
            <EventSetupSponsorsMobile />
          </EventEditorContextProvider>
        }
      />
      <Route path="/events/info/:eventId/setup/campaigns" element={<EventSetupCampaignsMobile />} />
      <Route path="/events/info/:eventId/setup/products" element={<EventSetupProductsMobile />} />
      <Route
        path="/events/info/:eventId/setup/moderation"
        element={<EventSetupModerationMobile />}
      />
      <Route
        path="/events/info/:eventId/setup/audience_configured"
        element={<EventSetupAudienceCatalogMobile />}
      />
      <Route
        path="/events/info/:eventId/setup/subdomain_verified"
        element={
          <EventEditorContextProvider>
            <EventSubdomainMobile />
          </EventEditorContextProvider>
        }
      />
      <Route
        path="/events/info/:eventId/setup/credential_granted"
        element={
          <EventEditorContextProvider>
            <EventSetupAccountsMobile />
          </EventEditorContextProvider>
        }
      />

      <Route
        path="/events/info/:eventId/setup/creative_templates"
        element={
          <CreativeTemplatesProvider>
            <EventSetupCreativeTemplatesMobile />
          </CreativeTemplatesProvider>
        }
      />
      {/** **/}
      {/*  */}

      <Route path="/events/info/:eventId/preview" element={<EventPreview />} />
      <Route path="/audiences" element={<AudiencesEventPage />} />
      <Route path="/audiences/:audienceId" element={<AudiencesEventPage />} />
      <Route path="/audiences/create" element={<AudiencesEditPage />} />
      <Route path="/audiences/edit/:audienceId" element={<AudiencesEditPage />} />
      <Route path="/audiences/segment/create" element={<SegmentEditPage />} />
      <Route path="/audiences/segment/edit/:segmentId" element={<SegmentEditPage />} />
      <Route path="/moderation/:tabId" element={<ModerationPage />} />
      <Route path="/moderation" element={<ModerationPage />} />
      <Route path="/partners" element={<PartnersPage />} />
      <Route path="/analytics" element={<AnalyticsPage />} />
      <Route path="/billing" element={<Navigate to="/settings/billing" />} />
      {/*COMMON */}
      <Route path="/email-confirmed" element={<EmailConfirmationSuccessPage />} />
      <Route path="/pass_recover" element={<PasswordRecoverPage />} />
      <Route path="/pass_recover/event" element={<PasswordRecoverPage />} />
      <Route path="/pass_recover/attendee" element={<PasswordRecoverPage />} />
      <Route path="/pass_recover_confirm" element={<PasswordChangeFromRedirectPage />} />
      <Route path="/login/link/:token" element={<LoginLinkPage />} />

      <Route path="/notifications" element={<NotificationsPage />} />

      <Route path="/settings" element={<SettingsPage isOrganizer />} />
      <Route path="/settings/:tabId" element={<SettingsPage isOrganizer />} />
      <Route path="/settings/:tabId/:billingId" element={<SettingsPage isOrganizer />} />
      <Route path="/settings/:tabId/:accountId" element={<SettingsPage isOrganizer />} />

      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/cookie-policy" element={<CookiePolicy />} />
      <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
      <Route path="/common-terms-and-conditions" element={<CommonTermsOfServicePage />} />
      <Route path="/sponsor-service-agreement" element={<SponsorServiceAgreementPage />} />
      <Route path="/event-service-agreement" element={<EventServiceAgreementPage />} />
      <Route
        path={'/onboarding/event'}
        element={
          <EventEditorContextProvider>
            <EventOnboarding />
          </EventEditorContextProvider>
        }
      />
      <Route
        path={'/onboarding/event/:accountId'}
        element={
          <EventEditorContextProvider>
            <EventOnboarding />
          </EventEditorContextProvider>
        }
      />
      {startRoutes()}
      {homeRedirects(ORGANIZATION_TYPE.organizer)}
    </Route>
  ),

  attendee: (
    <Route element={<PrivateRoutes />}>
      {startRoutes()}
      <Route element={<AttendeeIntentGate />}>
        <Route path="/events" element={<AttendeeEventsPage />} />
        <Route path="/shortlist" element={<AttendeeShortlistPage />} />
        <Route path="/matching" element={<AttendeeMatchesPage />} />
        <Route path="/chats" element={<AttendeeChatsPage />} />
        <Route path="/chats/:threadId" element={<AttendeeChatPage />} />
        <Route path="/companies/:matchId" element={<AttendeeCompanyPage />} />
        <Route path="/meetings" element={<AttendeeMeetingsPage />} />
        <Route path="/memory" element={<AttendeeMemoryPage />} />
        {/* the company profile moved into Memory — old links land there */}
        <Route path="/settings/company" element={<Navigate to="/memory" replace />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/settings/:tabId" element={<SettingsPage />} />
        <Route path="/notifications" element={<NotificationsPage />} />
        <Route path="/email-confirmed" element={<EmailConfirmationSuccessPage />} />
        <Route path="/pass_recover" element={<PasswordRecoverPage />} />
        <Route path="/pass_recover_confirm" element={<PasswordChangeFromRedirectPage />} />
        <Route path="/login/link/:token" element={<LoginLinkPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/cookie-policy" element={<CookiePolicy />} />
        <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
        <Route path="/common-terms-and-conditions" element={<CommonTermsOfServicePage />} />
        <Route path="/sponsor-service-agreement" element={<SponsorServiceAgreementPage />} />
        <Route path="/event-service-agreement" element={<EventServiceAgreementPage />} />
        {homeRedirects(ORGANIZATION_TYPE.attendee)}
      </Route>
    </Route>
  ),
};

type RoutesProps = {
  locationOverride?: Location;
};

const Routes = ({ locationOverride }: RoutesProps = {}) => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const { user: data, isUserLoading } = useAuth() as { user: UserType; isUserLoading: boolean };
  const routerLocation = useLocation();
  const location = locationOverride ?? routerLocation;
  const routeTitle =
    Object.entries(ROUTE_TITLES).find(([path]) => location.pathname.startsWith(path))?.[1] ?? '';
  useDocumentTitle(routeTitle ? `${routeTitle} - ${DEFAULT_TITLE}` : DEFAULT_TITLE);

  if (isUserLoading) {
    return <LoadingView />;
  }

  // not registered user
  if (!data || !data.email) {
    return (
      <Router location={location}>
        <Route path="/init" element={<InitPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/login/check" element={<LoginCheckPage />} />
        <Route path="/login/event" element={<LoginPage />} />
        {/* the attendee (Match.Express) face of login, reachable by path so it can
            be opened on the dev domain too — on mymatch.express the hostname
            alone selects it */}
        <Route path="/login/attendee" element={<LoginPage />} />

        <Route path="/pass_recover" element={<PasswordRecoverPage />} />
        <Route path="/pass_recover/event" element={<PasswordRecoverPage />} />
        <Route path="/pass_recover/attendee" element={<PasswordRecoverPage />} />
        <Route path="/pass_recover_confirm" element={<PasswordChangeFromRedirectPage />} />
        <Route path="/login/link/:token" element={<LoginLinkPage />} />
        <Route path="/registration" element={<RegistrationPage />} />
        <Route path="/registration/event" element={<RegistrationPage />} />
        <Route path="/registration/attendee" element={<RegistrationPage />} />
        <Route path="/m/:eventId/:partnerId" element={<MeetRedirect />} />

        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/cookie-policy" element={<CookiePolicy />} />
        <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
        <Route path="/common-terms-and-conditions" element={<CommonTermsOfServicePage />} />
        <Route path="/sponsor-service-agreement" element={<SponsorServiceAgreementPage />} />
        <Route path="/event-service-agreement" element={<EventServiceAgreementPage />} />
        <Route path="/email-confirmed" element={<EmailConfirmationSuccessPage />} />
        <Route path="/onboarding/partner/:hash" element={<PartnerOnboarding />} />
        {startRoutes()}

        <Route path={home} element={<Navigate to={login} />} />
        <Route path={'*'} element={<Navigate to={login} />} />
      </Router>
    );
  }

  // user during registration

  // onboarding event user
  if (data?.organization?.onboarding !== 'ready') {
    // event onboarding
    if (data?.organization?.type === ORGANIZATION_TYPE.organizer) {
      return (
        <Router location={location}>
          <Route path="/pass_recover_confirm" element={<PasswordChangeFromRedirectPage />} />
          <Route path="/login/link/:token" element={<LoginLinkPage />} />
          <Route path="/" element={<Navigate to="/onboarding/event" />} />
          <Route path="/init" element={<InitPage />} />
          <Route
            path={'/onboarding/event'}
            element={
              <EventEditorContextProvider>
                <EventOnboarding />
              </EventEditorContextProvider>
            }
          />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
          <Route path="/common-terms-and-conditions" element={<CommonTermsOfServicePage />} />
          <Route path="/sponsor-service-agreement" element={<SponsorServiceAgreementPage />} />
          <Route path="/event-service-agreement" element={<EventServiceAgreementPage />} />
          <Route path="/email-confirmed" element={<EmailConfirmationSuccessPage />} />
          {/* public and shareable — reachable even mid-onboarding */}
          {startRoutes()}
        </Router>
      );
    }
    // partner onboarding
    if (data?.organization?.type === 'partner') {
      return (
        <Router location={location}>
          <Route path="/pass_recover_confirm" element={<PasswordChangeFromRedirectPage />} />
          <Route path="/login/link/:token" element={<LoginLinkPage />} />
          <Route path="/init" element={<InitPage />} />

          <Route path="/onboarding/partner" element={<PartnerOnboarding />} />
          {/* before the catch-all: the start link is public and shareable */}
          {startRoutes()}
          <Route path="/*" element={<Navigate to="/onboarding/partner" />} />

          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
          <Route path="/common-terms-and-conditions" element={<CommonTermsOfServicePage />} />
          <Route path="/sponsor-service-agreement" element={<SponsorServiceAgreementPage />} />
          <Route path="/event-service-agreement" element={<EventServiceAgreementPage />} />
          <Route path="/email-confirmed" element={<EmailConfirmationSuccessPage />} />
        </Router>
      );
    }
    if (data && !data?.organization?.type) {
      return (
        <Router location={location}>
          <Route path={home} element={<Navigate to={login} />} />
          <Route path="/init" element={<InitPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/login/check" element={<LoginCheckPage />} />
          <Route path="/login/event" element={<LoginPage />} />
          {/* the attendee (Match.Express) face of login, reachable by path so it can
              be opened on the dev domain too — on mymatch.express the hostname
              alone selects it */}
          <Route path="/login/attendee" element={<LoginPage />} />

          <Route path="/pass_recover" element={<PasswordRecoverPage />} />
          <Route path="/pass_recover/event" element={<PasswordRecoverPage />} />
          <Route path="/pass_recover/attendee" element={<PasswordRecoverPage />} />
          <Route path="/pass_recover_confirm" element={<PasswordChangeFromRedirectPage />} />
          <Route path="/login/link/:token" element={<LoginLinkPage />} />

          <Route path="/registration" element={<RegistrationPage />} />
          <Route path="/registration/event" element={<RegistrationPage />} />
          <Route path="/registration/attendee" element={<RegistrationPage />} />
          <Route path="/m/:eventId/:partnerId" element={<MeetRedirect />} />

          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
          <Route path="/common-terms-and-conditions" element={<CommonTermsOfServicePage />} />
          <Route path="/sponsor-service-agreement" element={<SponsorServiceAgreementPage />} />
          <Route path="/event-service-agreement" element={<EventServiceAgreementPage />} />
          <Route path="/email-confirmed" element={<EmailConfirmationSuccessPage />} />
          {/* public and shareable — reachable even mid-onboarding */}
          {startRoutes()}
          <Route path={'*'} element={<NotFoundPage />} />
        </Router>
      );
    }
  }

  // registered user
  return data?.organization?.type ? (
    <Router location={location}>{OrganizationRoutesByType[data.organization.type]}</Router>
  ) : null;
};

export default Routes;
